import { FC, useState } from 'react';
import { PortfolioProperty, PortfolioPropertyFormData } from '@/types/index';

interface Props {
  initial?: PortfolioProperty;
  prefill?: PortfolioPropertyFormData;
  onSubmit: (data: PortfolioPropertyFormData) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
}

const fmtChf = (v: number | null | undefined): string => {
  if (v == null || isNaN(v)) return '—';
  return `CHF ${new Intl.NumberFormat('fr-CH').format(Math.round(v))}`;
};
const fmtPct = (v: number | null | undefined, d = 2): string => {
  if (v == null || isNaN(v)) return '—';
  return `${v.toFixed(d)} %`;
};

function useNum(initial?: number | null) {
  const [raw, setRaw] = useState(initial != null ? String(initial) : '');
  const val = raw === '' ? undefined : parseFloat(raw);
  return [raw, setRaw, val] as const;
}

const Field: FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  suffix?: string;
  hint?: string;
}> = ({ label, value, onChange, type = 'text', suffix, hint }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">{label}</label>
    <div className="relative flex items-center">
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      {suffix && (
        <span className="absolute right-3 text-xs text-gray-400 pointer-events-none">{suffix}</span>
      )}
    </div>
    {hint && <p className="mt-0.5 text-xs text-blue-600 dark:text-blue-400 font-medium">{hint}</p>}
  </div>
);

const SectionHeader: FC<{ title: string; open: boolean; onClick: () => void }> = ({ title, open, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-750 transition-colors"
  >
    {title}
    <svg className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  </button>
);

const CalcRow: FC<{ label: string; value: string; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div className={`flex justify-between items-center py-1 px-3 rounded ${highlight ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
    <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
    <span className={`text-xs font-semibold ${highlight ? 'text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300'}`}>{value}</span>
  </div>
);

export const PortfolioPropertyForm: FC<Props> = ({ initial, prefill, onSubmit, onCancel, isSubmitting }) => {
  const [sections, setSections] = useState({ general: true, revenus: true, charges: false, financement: false, valorisation: false });
  const toggle = (s: keyof typeof sections) => setSections(prev => ({ ...prev, [s]: !prev[s] }));

  // Source : données d'édition (initial) ou de pré-remplissage depuis une annonce (prefill)
  const src = initial ?? prefill;

  // Général
  const [name, setName] = useState(src?.name ?? '');
  const [propertyType, setPropertyType] = useState(src?.propertyType ?? 'residential');
  const [address, setAddress] = useState(src?.address ?? '');
  const [city, setCity] = useState(src?.city ?? '');
  const [postalCode, setPostalCode] = useState(src?.postalCode ?? '');
  const [canton, setCanton] = useState(src?.canton ?? '');
  const [acquisitionDate, setAcquisitionDate] = useState(
    src?.acquisitionDate ? src.acquisitionDate.substring(0, 10) : ''
  );
  const [acqPriceRaw, setAcqPriceRaw, acquisitionPrice] = useNum(src?.acquisitionPrice);
  const [areaRaw, setAreaRaw, area] = useNum(src?.area);
  const [roomsRaw, setRoomsRaw, rooms] = useNum(src?.rooms);
  const [notes, setNotes] = useState(src?.notes ?? '');

  // Revenus
  const [grossRentRaw, setGrossRentRaw, grossAnnualRent] = useNum(src?.grossAnnualRent);
  const [vacancyRaw, setVacancyRaw, vacancyRate] = useNum(src?.vacancyRate ?? 0);

  // Charges
  const [ppeRaw, setPpeRaw, ppeChargesAnnual] = useNum(src?.ppeChargesAnnual);
  const [maintRaw, setMaintRaw, maintenanceCosts] = useNum(src?.maintenanceCosts);
  const [taxRaw, setTaxRaw, propertyTax] = useNum(src?.propertyTax);
  const [otherRaw, setOtherRaw, otherCosts] = useNum(src?.otherCosts);

  // Financement
  const [debtRaw, setDebtRaw, currentDebt] = useNum(src?.currentDebt);
  const [rateRaw, setRateRaw, interestRate] = useNum(src?.interestRate);
  const [rateType, setRateType] = useState(src?.rateType ?? 'fixed');
  const [amortiRaw, setAmortiRaw, annualAmortization] = useNum(src?.annualAmortization);

  // Valorisation
  const [marketValRaw, setMarketValRaw, currentMarketValue] = useNum(src?.currentMarketValue);
  const [valuationMethod, setValuationMethod] = useState(src?.valuationMethod ?? 'market');

  // Calculs live
  const effectiveRent = (grossAnnualRent ?? 0) * (1 - (vacancyRate ?? 0) / 100);
  const totalCharges = (ppeChargesAnnual ?? 0) + (maintenanceCosts ?? 0) + (propertyTax ?? 0) + (otherCosts ?? 0);
  const NOI = effectiveRent - totalCharges;
  const annualInterest = (currentDebt ?? 0) * ((interestRate ?? 0) / 100);
  const cashFlow = NOI - annualInterest - (annualAmortization ?? 0);
  const LTV = currentMarketValue ? (currentDebt ?? 0) / currentMarketValue : null;
  const capRate = currentMarketValue ? (NOI / currentMarketValue) * 100 : null;
  const netYield = acquisitionPrice ? (NOI / acquisitionPrice) * 100 : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit({
      name,
      propertyId: src?.propertyId ?? undefined,
      propertyType,
      address: address || undefined,
      city: city || undefined,
      postalCode: postalCode || undefined,
      canton: canton || undefined,
      acquisitionDate: acquisitionDate || undefined,
      acquisitionPrice,
      area,
      rooms,
      notes: notes || undefined,
      grossAnnualRent,
      vacancyRate: vacancyRate ?? 0,
      ppeChargesAnnual,
      maintenanceCosts,
      propertyTax,
      otherCosts,
      currentDebt,
      interestRate,
      rateType,
      annualAmortization,
      currentMarketValue,
      valuationMethod,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[75vh] overflow-y-auto px-1">

      {/* Section Général */}
      <div className="space-y-3">
        <SectionHeader title="1. Données générales" open={sections.general} onClick={() => toggle('general')} />
        {sections.general && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pl-1">
            <div className="sm:col-span-2">
              <Field label="Nom de l'actif *" value={name} onChange={setName} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Type</label>
              <select
                value={propertyType}
                onChange={e => setPropertyType(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="residential">Résidentiel</option>
                <option value="commercial">Commercial</option>
                <option value="mixed">Mixte</option>
              </select>
            </div>
            <Field label="Date d'acquisition" value={acquisitionDate} onChange={setAcquisitionDate} type="date" />
            <Field label="Adresse" value={address} onChange={setAddress} />
            <Field label="Ville" value={city} onChange={setCity} />
            <Field label="NPA" value={postalCode} onChange={setPostalCode} />
            <Field label="Canton" value={canton} onChange={setCanton} />
            <Field label="Prix d'acquisition (CHF)" value={acqPriceRaw} onChange={setAcqPriceRaw} type="number" />
            <Field label="Surface (m²)" value={areaRaw} onChange={setAreaRaw} type="number" suffix="m²" />
            <Field label="Pièces" value={roomsRaw} onChange={setRoomsRaw} type="number" />
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Notes</label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>
          </div>
        )}
      </div>

      {/* Section Revenus */}
      <div className="space-y-3">
        <SectionHeader title="2. Revenus locatifs" open={sections.revenus} onClick={() => toggle('revenus')} />
        {sections.revenus && (
          <div className="space-y-3 pl-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Loyer brut annuel (CHF)" value={grossRentRaw} onChange={setGrossRentRaw} type="number"
                hint={grossAnnualRent ? `${fmtChf(grossAnnualRent / 12)} / mois` : undefined} />
              <Field label="Taux de vacance (%)" value={vacancyRaw} onChange={setVacancyRaw} type="number" suffix="%" />
            </div>
            <div className="space-y-1 rounded-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
              <CalcRow label="Revenu effectif annuel" value={fmtChf(effectiveRent)} highlight />
              <CalcRow label="Revenu effectif mensuel" value={fmtChf(effectiveRent / 12)} />
            </div>
          </div>
        )}
      </div>

      {/* Section Charges */}
      <div className="space-y-3">
        <SectionHeader title="3. Charges d'exploitation" open={sections.charges} onClick={() => toggle('charges')} />
        {sections.charges && (
          <div className="space-y-3 pl-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Charges PPE annuelles (CHF)" value={ppeRaw} onChange={setPpeRaw} type="number" />
              <Field label="Entretien annuel (CHF)" value={maintRaw} onChange={setMaintRaw} type="number" />
              <Field label="Impôt foncier annuel (CHF)" value={taxRaw} onChange={setTaxRaw} type="number" />
              <Field label="Autres coûts annuels (CHF)" value={otherRaw} onChange={setOtherRaw} type="number" />
            </div>
            <div className="space-y-1 rounded-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
              <CalcRow label="Total charges annuelles" value={fmtChf(totalCharges)} />
              <CalcRow label="NOI (Net Operating Income)" value={fmtChf(NOI)} highlight />
              {netYield != null && <CalcRow label="Rendement net" value={fmtPct(netYield)} />}
            </div>
          </div>
        )}
      </div>

      {/* Section Financement */}
      <div className="space-y-3">
        <SectionHeader title="4. Financement" open={sections.financement} onClick={() => toggle('financement')} />
        {sections.financement && (
          <div className="space-y-3 pl-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Dette actuelle (CHF)" value={debtRaw} onChange={setDebtRaw} type="number" />
              <Field label="Taux d'intérêt (%)" value={rateRaw} onChange={setRateRaw} type="number" suffix="%" />
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Type de taux</label>
                <select
                  value={rateType}
                  onChange={e => setRateType(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="fixed">Fixe</option>
                  <option value="variable">Variable</option>
                </select>
              </div>
              <Field label="Amortissement annuel (CHF)" value={amortiRaw} onChange={setAmortiRaw} type="number" />
            </div>
            <div className="space-y-1 rounded-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
              <CalcRow label="Intérêts annuels" value={fmtChf(annualInterest)} />
              <CalcRow label="Cash-flow annuel" value={fmtChf(cashFlow)} highlight />
              <CalcRow label="Cash-flow mensuel" value={fmtChf(cashFlow / 12)} />
            </div>
          </div>
        )}
      </div>

      {/* Section Valorisation */}
      <div className="space-y-3">
        <SectionHeader title="5. Valorisation" open={sections.valorisation} onClick={() => toggle('valorisation')} />
        {sections.valorisation && (
          <div className="space-y-3 pl-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Valeur de marché actuelle (CHF)" value={marketValRaw} onChange={setMarketValRaw} type="number" />
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Méthode de valorisation</label>
                <select
                  value={valuationMethod}
                  onChange={e => setValuationMethod(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="market">Valeur de marché</option>
                  <option value="yield">Rendement (NOI / cap rate)</option>
                </select>
              </div>
            </div>
            <div className="space-y-1 rounded-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
              {LTV != null && <CalcRow label="LTV" value={fmtPct(LTV * 100)} highlight />}
              {capRate != null && <CalcRow label="Cap rate" value={fmtPct(capRate)} />}
              {currentMarketValue && currentDebt != null && (
                <CalcRow label="Fonds propres" value={fmtChf(currentMarketValue - currentDebt)} />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-200 dark:border-gray-700 sticky bottom-0 bg-white dark:bg-gray-900 py-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={isSubmitting || !name.trim()}
          className="px-5 py-2 text-sm rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? 'Enregistrement...' : initial ? 'Mettre à jour' : 'Créer l\'actif'}
        </button>
      </div>
    </form>
  );
};
